/**
 * CanvasChoicePanel — the scene's choice panel drawn on the editor canvas
 * (spec 008), looking like the player's: the same layout (list/grid),
 * position (bottom/center) and legibility (dark fill, copper border, white
 * text in stage pixels, so it scales with the viewport).
 *
 * - Edit mode: drawn when the playhead is at or after the reveal time, or
 *   while a ChoiceEditor input has focus. Clicking an option focuses that
 *   option's text input in the ChoiceEditor.
 * - Preview: drawn while Preview holds at the reveal; clicking an option ends
 *   the preview and the caller shows "Chose: …".
 *
 * With position `bottom` the panel's lower edge sits at `bottomPx`, which
 * SceneCanvas computes with 020's `choicePanelBottom` from the dialog boxes
 * it drew (10 px above the highest bottom box, 16 px with none; spec 017), so
 * it never overlaps a dialog. Only the buttons take pointer events.
 */

import React, { useEffect, useState } from 'react';
import type { ChoiceOption, ChoiceSettings } from '../../../../../Shared/src/@types';

/** The panel's inset from the stage's side edges. */
const PANEL_INSET_PX = 12;

/** Marks the ChoiceEditor root, so focus inside it can be detected. */
export const CHOICE_EDITOR_ATTR = 'data-choice-editor';

const insideChoiceEditor = (el: EventTarget | null) =>
  el instanceof Element && el.closest(`[${CHOICE_EDITOR_ATTR}]`) !== null;

/** True while any input inside the ChoiceEditor has focus. */
export function useChoiceEditorFocused(): boolean {
  const [focused, setFocused] = useState(() => insideChoiceEditor(document.activeElement));
  useEffect(() => {
    const onIn = (e: FocusEvent) => setFocused(insideChoiceEditor(e.target));
    const onOut = (e: FocusEvent) => {
      if (!insideChoiceEditor(e.relatedTarget)) setFocused(false);
    };
    document.addEventListener('focusin', onIn);
    document.addEventListener('focusout', onOut);
    return () => {
      document.removeEventListener('focusin', onIn);
      document.removeEventListener('focusout', onOut);
    };
  }, []);
  return focused;
}

/** Focus the ChoiceEditor's text input for this option. */
export function focusChoiceInput(choiceId: string): void {
  const input = document.querySelector<HTMLInputElement>(
    `[${CHOICE_EDITOR_ATTR}] input[data-choice-id="${CSS.escape(choiceId)}"]`,
  );
  input?.focus();
  input?.select();
}

interface CanvasChoicePanelProps {
  choices: ChoiceOption[];
  settings: ChoiceSettings | undefined;
  /** Distance of a `bottom` panel's lower edge from the stage bottom (stage px). */
  bottomPx: number;
  onOptionClick: (choice: ChoiceOption) => void;
}

const CanvasChoicePanel: React.FC<CanvasChoicePanelProps> = ({ choices, settings, bottomPx, onOptionClick }) => {
  const layout = settings?.layout ?? 'list';
  const centred = (settings?.position ?? 'bottom') === 'center';
  const bottom = centred ? '50%' : bottomPx;

  return (
    <div
      data-testid="canvas-choice-panel"
      data-canvas-chrome
      style={{
        position: 'absolute',
        bottom,
        left: PANEL_INSET_PX,
        right: PANEL_INSET_PX,
        transform: centred ? 'translateY(50%)' : undefined,
        display: 'flex',
        flexDirection: layout === 'grid' ? 'row' : 'column',
        flexWrap: layout === 'grid' ? 'wrap' : 'nowrap',
        gap: 6,
        pointerEvents: 'none',
        zIndex: 900,
      }}
    >
      {choices.map((c, i) => (
        <button
          key={c.id}
          type="button"
          data-testid="canvas-choice-option"
          data-choice-id={c.id}
          // Keep focus where it is: a press must not blur the ChoiceEditor input
          onMouseDown={(e) => e.preventDefault()}
          onPointerDown={(e) => e.stopPropagation()}
          onClick={(e) => {
            e.stopPropagation();
            onOptionClick(c);
          }}
          style={{
            flex: layout === 'grid' ? '1 1 45%' : undefined,
            padding: '10px 16px',
            background: 'rgba(18, 12, 5, 0.82)',
            border: '1.5px solid rgba(184, 115, 51, 0.8)',
            borderRadius: 6,
            color: c.text.trim() ? '#fff' : 'rgba(255, 255, 255, 0.45)',
            fontSize: 14,
            lineHeight: 1.3,
            cursor: 'pointer',
            textAlign: 'center',
            overflowWrap: 'anywhere',
            pointerEvents: 'auto',
          }}
        >
          {c.text.trim() || `Option ${i + 1}`}
        </button>
      ))}
    </div>
  );
};

export default CanvasChoicePanel;
